import { ReloadOutlined } from "@ant-design/icons";
import { Button, Modal, Skeleton, Tag, Typography } from "antd";
import type React from "react";
import type { TransferTarget, TransferTargetOption } from "../../types/index.type";
import { type TargetEntry, freshnessCopy, groupByDay, unavailableCopy } from "./transferTarget";

const { Text } = Typography;

const QUIET: React.CSSProperties = { fontSize: 12, color: "#595959" };
const ROW_MIN_HEIGHT = 44;

interface TransferTargetMenuProps {
  entry: TargetEntry;
  // Row click: start (or restart) the transfer to this appointment.
  onPick: (option: TransferTargetOption) => void;
  onRefresh: () => void;
  // Disables rows while a kickoff is in flight.
  busy?: boolean;
  width?: number | string;
}

/**
 * The caret's dropdown: the full schedule, grouped by day, the suggestion
 * marked, rows that already carry a note or are signed tagged (and confirmed
 * on pick), plus the empty / unavailable / loading states. Rows never reorder
 * while the menu is open — the entry is frozen by the caller.
 */
const TransferTargetMenu: React.FC<TransferTargetMenuProps> = ({
  entry,
  onPick,
  onRefresh,
  busy = false,
  width = 360,
}) => {
  const target = entry.target;

  const shell = (children: React.ReactNode, footer?: React.ReactNode) => (
    <div
      role="menu"
      aria-label="Choose the appointment to transfer to"
      style={{
        width,
        maxWidth: "calc(100vw - 32px)",
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        boxShadow: "0 8px 24px rgba(0,0,0,.12)",
        overflow: "hidden",
      }}
    >
      <div style={{ maxHeight: 360, overflowY: "auto" }}>{children}</div>
      {footer}
    </div>
  );

  const footer = (copy: string, actionLabel: string) => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "6px 12px",
        borderTop: "1px solid #e5e7eb",
        background: "#f9fafb",
      }}
    >
      <Text style={QUIET} aria-live="polite">
        {copy}
      </Text>
      <Button size="small" type="link" icon={<ReloadOutlined />} onClick={onRefresh} disabled={entry.fetching || busy}>
        {actionLabel}
      </Button>
    </div>
  );

  if (!target || target.state === "loading" || (entry.fetching && !entry.settled)) {
    return shell(
      <div style={{ padding: "12px 14px" }} role="status">
        <Skeleton active paragraph={{ rows: 2 }} title={false} />
        <Text style={QUIET}>
          {entry.slow
            ? "Still reading the schedule from the EHR. Transfer works now — you’ll pick the appointment on the way."
            : "Loading the schedule…"}
        </Text>
      </div>,
    );
  }

  if (target.state === "unavailable") {
    return shell(
      <div style={{ padding: "12px 14px" }} role="status">
        <Text>{unavailableCopy(target.reason)}</Text>
        <div style={{ marginTop: 4 }}>
          <Text style={QUIET}>Transfer still works — you’ll pick the appointment on the way.</Text>
        </div>
      </div>,
      footer("", "Try again"),
    );
  }

  if (target.state === "empty" || target.options.length === 0) {
    return shell(
      <div style={{ padding: "12px 14px" }} role="status">
        <Text>No appointments found for this patient.</Text>
        {target.mrn && (
          <div style={{ marginTop: 4 }}>
            <Text style={QUIET}>MRN/Patient ID: {target.mrn}</Text>
          </div>
        )}
      </div>,
      footer(freshnessCopy(target.fetched_at), "Check again"),
    );
  }

  // A signed encounter refuses writes in most EHRs, so picking one asks first.
  // An encounter that already has a note does not: writing a second note into
  // the same visit is routine (about one transfer in five on some groups).
  const confirmThenPick = (option: TransferTargetOption) => {
    if (busy) return;
    if (!option.signed) {
      onPick(option);
      return;
    }
    Modal.confirm({
      title: "Transfer to this appointment?",
      content: "This appointment is signed in the EHR. Transfer anyway?",
      okText: "Transfer anyway",
      cancelText: "Keep looking",
      onOk: () => onPick(option),
    });
  };

  return shell(
    <div>
      {groupByDay(target.options).map((group) => (
        <div key={group.label}>
          <div
            style={{
              fontSize: 11,
              color: "#6b7280",
              padding: "8px 12px 4px",
              textTransform: "uppercase",
              letterSpacing: ".04em",
            }}
          >
            {group.label}
          </div>
          {group.options.map((option) => (
            <TargetRow
              key={option.id}
              option={option}
              suggested={option.id === target.suggested_id}
              disabled={busy}
              onClick={() => confirmThenPick(option)}
            />
          ))}
        </div>
      ))}
    </div>,
    footer(freshnessCopy(target.fetched_at), "Refresh"),
  );
};

const TargetRow: React.FC<{
  option: TransferTargetOption;
  suggested: boolean;
  disabled: boolean;
  onClick: () => void;
}> = ({ option, suggested, disabled, onClick }) => {
  const details = [option.visit_type, option.provider, option.location || option.department]
    .filter(Boolean)
    .join(" · ");
  const muted = option.signed;
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      disabled={disabled}
      aria-label={`${option.day_label || option.date} ${option.time} ${details}`.trim()}
      style={{
        width: "100%",
        minHeight: ROW_MIN_HEIGHT,
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 12px",
        border: 0,
        borderTop: "1px solid #f3f4f6",
        background: suggested ? "#eef2ff" : "transparent",
        cursor: disabled ? "default" : "pointer",
        textAlign: "left",
        opacity: muted ? 0.55 : 1,
      }}
    >
      <span style={{ fontWeight: 600, minWidth: 74, color: "#111827" }}>
        {option.time || option.day_label || option.date}
      </span>
      <span style={{ flex: 1, minWidth: 0, ...QUIET, overflow: "hidden", textOverflow: "ellipsis" }}>{details}</span>
      {suggested && <Tag color="purple">Suggested</Tag>}
      {option.transferred_note_id && <Tag color="green">Note transferred</Tag>}
      {option.signed && <Tag>Signed</Tag>}
      {option.compile === "pending" && !option.transferred_note_id && (
        <Text style={{ ...QUIET, fontStyle: "italic" }}>preparing…</Text>
      )}
    </button>
  );
};

export default TransferTargetMenu;
